import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { TrendingUp, Shield, Users, ArrowRight, ChevronRight, Star, CheckCircle2, Clock, Zap, Lock, BarChart3, Wallet } from "lucide-react";
import { formatPrice } from "../lib/utils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reviews = [
  {
    name: "Carlos M.",
    location: "Madrid, Espana",
    rating: 5,
    text: "Increible plataforma. En solo 2 semanas he generado mas de 3.500EUR siguiendo las operaciones de Andres. Muy facil de usar.",
    profit: "+3.500EUR",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
  },
  {
    name: "Maria L.",
    location: "Barcelona, Espana",
    rating: 5,
    text: "Al principio tenia dudas, pero los resultados hablan por si solos. Mi inversion inicial de 1.000EUR ya vale 2.800EUR.",
    profit: "+1.800EUR",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
  },
  {
    name: "Javier R.",
    location: "Valencia, Espana",
    rating: 5,
    text: "Llevo 3 meses copiando a Andres y no puedo estar mas satisfecho. Ganancias constantes y sin complicaciones.",
    profit: "+8.200EUR",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
  },
  {
    name: "Ana G.",
    location: "Sevilla, Espana",
    rating: 5,
    text: "La mejor decision que he tomado. El soporte es excelente y las ganancias son reales. 100% recomendado.",
    profit: "+4.100EUR",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
  },
  {
    name: "Pedro S.",
    location: "Bilbao, Espana",
    rating: 5,
    text: "Nunca pense que el trading pudiera ser tan sencillo. Solo activas el copy trading y ves crecer tu dinero.",
    profit: "+5.600EUR",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
  },
  {
    name: "Laura T.",
    location: "Malaga, Espana",
    rating: 5,
    text: "Mi marido y yo invertimos juntos. En 6 semanas hemos duplicado nuestra inversion. Andres es un genio.",
    profit: "+6.000EUR",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop"
  }
];

const profitExamples = [
  { investment: 500, days: 7, profit: 175, total: 675 },
  { investment: 1000, days: 14, profit: 520, total: 1520 },
  { investment: 2500, days: 30, profit: 1875, total: 4375 },
  { investment: 5000, days: 30, profit: 3750, total: 8750 },
  { investment: 10000, days: 30, profit: 7500, total: 17500 },
];

const formatEURLocal = (value) => {
  return value.toLocaleString('es-ES') + '\u20AC';
};

export default function Landing() {
  const navigate = useNavigate();
  
  const defaultPrices = [
    { symbol: 'BTC', current_price: 76500, price_change_percentage_24h: 1.25 },
    { symbol: 'ETH', current_price: 2580, price_change_percentage_24h: 2.15 },
    { symbol: 'BNB', current_price: 755, price_change_percentage_24h: -0.85 },
    { symbol: 'SOL', current_price: 112, price_change_percentage_24h: 3.42 },
    { symbol: 'XRP', current_price: 1.68, price_change_percentage_24h: 2.78 },
    { symbol: 'ADA', current_price: 0.58, price_change_percentage_24h: 1.95 },
    { symbol: 'DOGE', current_price: 0.11, price_change_percentage_24h: 4.12 },
    { symbol: 'DOT', current_price: 6.85, price_change_percentage_24h: 2.35 },
  ];
  
  const [prices, setPrices] = useState(defaultPrices);
  const [activeUsers, setActiveUsers] = useState(847);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/dashboard");
    
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/crypto/prices`);
        if (res.data.prices && res.data.prices.length > 0) {
          setPrices(res.data.prices);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);

    // Simulate active users counter
    const usersInterval = setInterval(() => {
      setActiveUsers(prev => prev + Math.floor(Math.random() * 3));
    }, 8000);

    return () => { clearInterval(interval); clearInterval(usersInterval); };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0B0E11]" data-testid="landing-page">
      {/* Ticker Tape */}
      <div className="bg-[#181A20] border-b border-[#2B3139] py-2 overflow-hidden">
        <div className="ticker-tape">
          <div className="ticker-tape-content opacity-85">
            {[...prices, ...prices, ...prices].map((coin, i) => (
              <div key={i} className="inline-flex items-center gap-2 mx-6 flex-shrink-0">
                <span className="text-[#848E9C] text-sm">{coin.symbol}</span>
                <span className="font-mono text-sm text-[#EAECEF]">
                  {formatPrice(coin.current_price)}{'\u20AC'}
                </span>
                <span className={`font-mono text-xs ${coin.price_change_percentage_24h >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                  {coin.price_change_percentage_24h >= 0 ? '+' : ''}{coin.price_change_percentage_24h?.toFixed(2).replace('.', ',')}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FCD535] rounded-sm flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#0B0E11]" />
              </div>
              <span className="text-xl font-bold text-[#EAECEF]">CopiaTrade.io</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/login">
                <Button variant="outline" className="text-[#EAECEF] border-[#FCD535] hover:bg-[#FCD535] hover:text-[#0B0E11] font-semibold px-3 sm:px-6 text-sm sm:text-base" data-testid="login-btn">
                  Iniciar Sesion
                </Button>
              </Link>
              <Link to="/register" className="hidden sm:block">
                <Button className="btn-primary font-bold px-8" data-testid="register-btn">
                  Empezar Gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0ECB81]/5 via-transparent to-[#FCD535]/5" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#0ECB81]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FCD535]/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              {/* Live badge */}
              <div className="inline-flex items-center gap-2 bg-[#0ECB81]/10 border border-[#0ECB81]/30 rounded-full px-4 py-2">
                <span className="w-2 h-2 bg-[#0ECB81] rounded-full animate-pulse" />
                <span className="text-sm text-[#0ECB81] font-semibold">{activeUsers.toLocaleString('es-ES')} personas invirtiendo ahora</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#EAECEF] leading-tight">
                Empieza a <span className="text-[#0ECB81]">ganar dinero</span> sin hacer nada
              </h1>
              
              <p className="text-base sm:text-lg text-[#848E9C] max-w-xl leading-relaxed">
                Copia automaticamente las operaciones de <span className="text-[#EAECEF] font-semibold">Andres Martin</span>, 
                uno de los traders mas exitosos del mercado con un 
                <span className="text-[#0ECB81] font-bold"> 92,3% de acierto</span>. 
                Solo deposita, activa y empieza a generar ingresos.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button className="btn-primary text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-7 w-full" data-testid="hero-cta">
                    <Zap className="w-5 h-5 mr-2" />
                    Quiero Empezar a Ganar
                  </Button>
                </Link>
                <Link to="/login" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-[#2B3139] text-[#EAECEF] hover:bg-[#2B3139] text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-7 w-full">
                    Ya tengo cuenta
                  </Button>
                </Link>
              </div>
              
              {/* Trust indicators */}
              <div className="grid grid-cols-3 gap-4 sm:flex sm:items-center sm:gap-8 pt-4">
                <div className="text-center sm:text-left">
                  <p className="text-lg sm:text-2xl font-bold font-mono text-[#0ECB81]">847K{'\u20AC'}+</p>
                  <p className="text-xs sm:text-sm text-[#848E9C]">Ganancias generadas</p>
                </div>
                <div className="hidden sm:block w-px h-12 bg-[#2B3139]" />
                <div className="text-center sm:text-left">
                  <p className="text-lg sm:text-2xl font-bold font-mono text-[#EAECEF]">15.432</p>
                  <p className="text-xs sm:text-sm text-[#848E9C]">Inversores activos</p>
                </div>
                <div className="hidden sm:block w-px h-12 bg-[#2B3139]" />
                <div className="text-center sm:text-left">
                  <p className="text-lg sm:text-2xl font-bold font-mono text-[#FCD535]">92,3%</p>
                  <p className="text-xs sm:text-sm text-[#848E9C]">Tasa de exito</p>
                </div>
              </div>
            </div>
            
            {/* Broker Card */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#FCD535]/20 to-[#0ECB81]/20 rounded-xl blur-lg" />
              <div className="relative card-dark p-4 sm:p-6 space-y-4 sm:space-y-5">
                <div className="flex items-center gap-3 sm:gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_15d8ac38-293d-45ed-a585-187f28b97866/artifacts/bpkxytwu_IMG_9072.jpeg"
                    alt="Andres Martin"
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-[#FCD535]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg sm:text-xl font-semibold text-[#EAECEF]">Andres Martin</h3>
                      <CheckCircle2 className="w-5 h-5 text-[#0ECB81] fill-[#0ECB81]/20" />
                    </div>
                    <p className="text-xs sm:text-sm text-[#848E9C]">Senior Broker - 8 anos de experiencia</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-1 bg-[#0ECB81]/20 text-[#0ECB81] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      <span className="w-2 h-2 bg-[#0ECB81] rounded-full animate-pulse" />
                      Online
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div className="bg-[#0B0E11] rounded-lg p-3 text-center">
                    <p className="text-lg sm:text-2xl font-bold font-mono text-[#0ECB81]">+5,2%</p>
                    <p className="text-[10px] sm:text-xs text-[#848E9C]">Promedio/op</p>
                  </div>
                  <div className="bg-[#0B0E11] rounded-lg p-3 text-center">
                    <p className="text-lg sm:text-2xl font-bold font-mono text-[#EAECEF]">2.847</p>
                    <p className="text-[10px] sm:text-xs text-[#848E9C]">Operaciones</p>
                  </div>
                  <div className="bg-[#0B0E11] rounded-lg p-3 text-center">
                    <p className="text-lg sm:text-2xl font-bold font-mono text-[#FCD535]">92,3%</p>
                    <p className="text-[10px] sm:text-xs text-[#848E9C]">Win Rate</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-xs sm:text-sm text-[#848E9C]">Ultimas operaciones en vivo:</p>
                  {[
                    { symbol: 'BTC/USDT', profit: '+3,53%', amount: '15.000\u20AC', type: 'LONG' },
                    { symbol: 'ETH/USDT', profit: '+5,18%', amount: '8.500\u20AC', type: 'LONG' },
                    { symbol: 'SOL/USDT', profit: '+8,86%', amount: '5.200\u20AC', type: 'LONG' },
                  ].map((trade, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#0B0E11] rounded-lg p-2.5 sm:p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-[#0ECB81]/10 flex items-center justify-center">
                          <TrendingUp className="w-3 h-3 text-[#0ECB81]" />
                        </div>
                        <span className="text-[#EAECEF] font-medium text-sm">{trade.symbol}</span>
                      </div>
                      <span className="font-mono text-[#848E9C] text-xs">{trade.amount}</span>
                      <span className="font-mono text-[#0ECB81] font-bold text-sm">{trade.profit}</span>
                    </div>
                  ))}
                </div>
                
                <Link to="/register" className="block">
                  <Button className="btn-primary w-full py-5 sm:py-6 text-base sm:text-lg" data-testid="copy-cta">
                    Copiar Sus Operaciones <ChevronRight className="ml-2" />
                  </Button>
                </Link>
                <p className="text-center text-xs text-[#848E9C]">Sin comisiones ocultas - Retira cuando quieras</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple 3-Step Process */}
      <section className="py-12 sm:py-20 relative overflow-hidden bg-[#181A20]/50">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EAECEF] mb-3">Tan facil como 1, 2, 3</h2>
            <p className="text-sm sm:text-base text-[#848E9C] max-w-2xl mx-auto">
              No necesitas saber nada de trading. Solo sigue estos 3 pasos y tu dinero trabaja por ti.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: "1", icon: Wallet, title: "Crea tu cuenta gratis", desc: "Registrate en menos de 1 minuto. Solo necesitas tu email y una contrasena." },
              { step: "2", icon: ArrowRight, title: "Deposita desde 50\u20AC", desc: "Anade fondos facilmente con tarjeta o Bitcoin. El proceso es rapido y seguro." },
              { step: "3", icon: Zap, title: "Activa y gana", desc: "Pulsa un boton y empieza a generar ganancias automaticamente. Asi de simple." },
            ].map((item, i) => (
              <div key={i} className="relative text-center group">
                {i < 2 && (
                  <div className="hidden sm:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-[#2B3139]" />
                )}
                <div className="w-16 h-16 bg-gradient-to-br from-[#FCD535] to-[#F0B90B] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#FCD535]/20 group-hover:scale-110 transition-transform">
                  <item.icon className="w-7 h-7 text-[#0B0E11]" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-[#EAECEF] mb-2">{item.title}</h3>
                <p className="text-sm text-[#848E9C] max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-10 sm:mt-14 text-center">
            <Link to="/register">
              <Button className="btn-primary text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7" data-testid="steps-cta">
                Crear Mi Cuenta Gratis <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="mt-3 text-xs text-[#848E9C]">Registro gratuito - Sin compromiso - Retira cuando quieras</p>
          </div>
        </div>
      </section>

      {/* Profit Calculator */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EAECEF] mb-3">Mira cuanto podrias ganar</h2>
            <p className="text-sm sm:text-base text-[#848E9C] max-w-2xl mx-auto">
              Estos son ejemplos reales de rentabilidad basados en el historial de Andres Martin
            </p>
          </div>
          
          {/* Mobile Cards */}
          <div className="sm:hidden space-y-3">
            {profitExamples.map((example, i) => (
              <div key={i} className="card-dark p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#848E9C] text-sm">Inviertes</span>
                  <span className="font-mono text-[#EAECEF] text-lg font-semibold">{formatEURLocal(example.investment)}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#848E9C] text-sm">En {example.days} dias ganas</span>
                  <span className="font-mono text-[#0ECB81] text-lg font-semibold">+{formatEURLocal(example.profit)}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-[#2B3139]">
                  <span className="text-[#FCD535] text-sm font-semibold">Tu balance sera</span>
                  <span className="font-mono text-[#FCD535] text-xl font-bold">{formatEURLocal(example.total)}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop Table */}
          <div className="hidden sm:block card-dark overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B0E11]">
                  <th className="text-left py-4 px-6 text-[#848E9C] font-medium text-sm">Tu Inversion</th>
                  <th className="text-left py-4 px-6 text-[#848E9C] font-medium text-sm">Periodo</th>
                  <th className="text-right py-4 px-6 text-[#848E9C] font-medium text-sm">Ganancia Estimada</th>
                  <th className="text-right py-4 px-6 text-[#848E9C] font-medium text-sm">Balance Final</th>
                </tr>
              </thead>
              <tbody>
                {profitExamples.map((example, i) => (
                  <tr key={i} className="border-b border-[#2B3139]/50 hover:bg-[#0B0E11]/50 transition-colors">
                    <td className="py-4 px-6">
                      <span className="font-mono text-[#EAECEF] text-lg">{formatEURLocal(example.investment)}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-[#848E9C]">{example.days} dias</span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <span className="font-mono text-[#0ECB81] text-lg font-semibold">+{formatEURLocal(example.profit)}</span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <span className="font-mono text-[#FCD535] text-lg font-bold">{formatEURLocal(example.total)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-center text-xs text-[#848E9C] mt-4">
            * Las ganancias pueden variar segun las condiciones del mercado. Rendimientos pasados no garantizan resultados futuros.
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-20 bg-[#181A20]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EAECEF] mb-3">Por que miles de personas confian en nosotros</h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: Zap, title: "Ganancias Automaticas", description: "Tu dinero genera ganancias 24/7 sin que tengas que hacer nada.", color: "#0ECB81" },
              { icon: Shield, title: "Fondos Seguros", description: "Tu capital esta protegido con la mas alta seguridad del mercado.", color: "#FCD535" },
              { icon: Clock, title: "Sin Experiencia", description: "No necesitas saber de trading. Nosotros nos encargamos de todo.", color: "#0ECB81" },
              { icon: BarChart3, title: "92,3% Win Rate", description: "Andres Martin tiene uno de los mejores historiales verificados.", color: "#FCD535" },
            ].map((feature, i) => (
              <Card key={i} className="card-dark hover:border-[#FCD535]/30 transition-all duration-300 group">
                <CardContent className="p-4 sm:p-6">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4`} style={{ backgroundColor: `${feature.color}15` }}>
                    <feature.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: feature.color }} />
                  </div>
                  <h3 className="text-sm sm:text-base font-semibold text-[#EAECEF] mb-1 sm:mb-2">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-[#848E9C] leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EAECEF] mb-3">Lo que dicen nuestros inversores</h2>
            <p className="text-sm sm:text-base text-[#848E9C]">
              +15.000 personas ya estan generando ingresos con CopiaTrade.io
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {reviews.map((review, i) => (
              <Card key={i} className="card-dark hover:border-[#2B3139] transition-colors">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-[#2B3139]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#EAECEF] text-sm sm:text-base">{review.name}</p>
                      <p className="text-xs text-[#848E9C] truncate">{review.location}</p>
                    </div>
                    <span className="font-mono text-[#0ECB81] font-bold text-sm sm:text-base">{review.profit}</span>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(review.rating)].map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-[#FCD535] text-[#FCD535]" />
                    ))}
                  </div>
                  <p className="text-[#848E9C] text-xs sm:text-sm leading-relaxed">"{review.text}"</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#0ECB81]">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Inversor verificado</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0ECB81]/5 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#0ECB81]/10 border border-[#0ECB81]/30 rounded-full px-4 py-2 mb-6">
            <Lock className="w-4 h-4 text-[#0ECB81]" />
            <span className="text-sm text-[#0ECB81] font-medium">Plazas limitadas - Registrate ahora</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#EAECEF] mb-4">
            Tu dinero podria estar trabajando por ti ahora mismo
          </h2>
          <p className="text-sm sm:text-lg text-[#848E9C] mb-8 max-w-xl mx-auto">
            Cada minuto que pasa es dinero que dejas de ganar. Unete hoy y empieza a ver resultados desde el primer dia.
          </p>
          <Link to="/register">
            <Button className="btn-primary text-base sm:text-lg px-10 sm:px-14 py-6 sm:py-7" data-testid="final-cta">
              <Zap className="w-5 h-5 mr-2" />
              Empezar a Ganar Dinero <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <div className="flex flex-wrap justify-center gap-6 mt-6 text-xs sm:text-sm text-[#848E9C]">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#0ECB81]" />
              <span>Registro gratuito</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#0ECB81]" />
              <span>Retira cuando quieras</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#0ECB81]" />
              <span>Soporte 24/7</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2B3139] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#FCD535] rounded-sm flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[#0B0E11]" />
              </div>
              <span className="text-sm text-[#848E9C]">CopiaTrade.io {'\u00A9'} 2025</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#848E9C]">
              <span>Terminos de Servicio</span>
              <span>Politica de Privacidad</span>
              <span>Contacto</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
